<!--
  This is for finding a restaurant via places api search
  selected restaurant is checked against our internal database
  options are then given to add if not there or use if it is.
-->

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import { storeToRefs } from 'pinia'
import { useRestaurantsStore } from '@/stores'
import GooglePlacesService from '@/services/GooglePlacesService'

const props = defineProps({
  restaurantName: {
    type: Array,
    required: true,
  },
  restaurantMarket: {
    type: String,
    required: true,
  },
})

const emit = defineEmits([
  'cancel',
  'confirm',
  'update:isDialogVisible',
])

const scroller = ref(null)
const restaurantPlaceArray = ref([])
const restaurantImageAttributionHtml = ref('')

const restaurantsStore = useRestaurantsStore()
const { restaurants, viewingDetailsRestaurant, viewingDetailsRestaurantTab } = storeToRefs(restaurantsStore)

// Google Places service instance
const googlePlacesService = new GooglePlacesService()

// Cache for fetched Google Photos - Map of googlePlaceId -> photoUrl
const fetchedGooglePhotos = new Map()
const fetchingGooglePhotos = new Set()

// Helper to get Google Places photo URL
const getGooglePhotoUrl = (photoReference, maxWidth = 200, maxHeight = 200) => {
  const apiKey = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY
  
  return `https://d128sk95sboo22.cloudfront.net/maps/api/place/photo?maxwidth=${maxWidth}&maxheight=${maxHeight}&photo_reference=${photoReference}&key=${apiKey}`
}

// Fetch Google Places photo on-demand
const fetchGooglePlacePhoto = async restaurant => {
  const googlePlaceId = restaurant.googlePlaceId

  if (!googlePlaceId) return null
  if (fetchingGooglePhotos.has(googlePlaceId)) return null
  if (fetchedGooglePhotos.has(googlePlaceId)) return fetchedGooglePhotos.get(googlePlaceId)

  fetchingGooglePhotos.add(googlePlaceId)
  try {
    const placeDetails = await googlePlacesService.getPlaceDetails(googlePlaceId, ['photos'])
    if (placeDetails?.photos?.length > 0) {
      const photoRef = placeDetails.photos[0].photo_reference
      if (photoRef) {
        const photoUrl = getGooglePhotoUrl(photoRef)
        fetchedGooglePhotos.set(googlePlaceId, photoUrl)
        
        return photoUrl
      }
    }
  } catch (error) {
    console.error('Failed to fetch Google Places photo:', error)
  } finally {
    fetchingGooglePhotos.delete(googlePlaceId)
  }
  
  return null
}

// Get restaurant image URL with fallback logic
const getRestaurantImageUrl = async restaurant => {
  // 1. Use uploaded images from database
  if (restaurant.imageIds?.length) {
    return {
      mainImageUrl: mediaUrl(restaurant.imageIds[0], 'thumb'),
      attribution: ''
    }
  }

  // 2. Fallback to event spaces images
  if (restaurant.eventSpaces?.length > 0) {
    const spaceWithImage = restaurant.eventSpaces.find(space => space.imageId)
    if (spaceWithImage) {
      return {
        mainImageUrl: mediaUrl(spaceWithImage.imageId, 'thumb'),
        attribution: ''
      }
    }
  }

  // 3. Fallback to cached Google Places photos
  if (restaurant.googlePlaceDetails?.photos?.length > 0) {
    const photoRef = restaurant.googlePlaceDetails.photos[0].photo_reference
    if (photoRef) {
      return {
        mainImageUrl: getGooglePhotoUrl(photoRef),
        attribution: restaurant.googlePlaceDetails.photos[0].html_attributions?.[0] || ''
      }
    }
  }

  // 4. Fallback to on-demand fetched Google Places photo
  if (restaurant.googlePlaceId) {
    const photoUrl = await fetchGooglePlacePhoto(restaurant)
    if (photoUrl) {
      return {
        mainImageUrl: photoUrl,
        attribution: ''
      }
    }
  }

  return null
}

// Extracted function for updating restaurant images
const updateRestaurantImages = async () => {
  const newArray = []

  for (let name of props.restaurantName) {
    const theRestaurant = restaurants.value.find(
      restaurant => (restaurant.name == name && restaurant.market == props.restaurantMarket)
    )

    if (theRestaurant) {
      // Use fallback logic to get restaurant image
      const imageData = await getRestaurantImageUrl(theRestaurant)
      if (imageData) {
        newArray.push(imageData)
      }
    }
  }

  restaurantPlaceArray.value = newArray
  restaurantImageAttributionHtml.value = newArray[0]?.attribution || ''

  if (restaurantPlaceArray.value.length >= 3) {
    if (scroller.value) {
      scroller.value.scrollLeft = scroller.value.clientWidth * 0.9
      setTimeout(() => {
        scroller.value.style.scrollBehavior = 'smooth'
      }, 100)
    }
  }
}

// Watcher to react to restaurants being loaded
watch(() => restaurants.value, async (newVal, oldVal) => {
  if (newVal?.length == oldVal?.length) {
    return
  }
  await updateRestaurantImages()
}, { immediate: true })

// Watchers for restaurantName and restaurantMarket
watch(() => props.restaurantName, updateRestaurantImages, { immediate: true })
watch(() => props.restaurantMarket, updateRestaurantImages, { immediate: true })

onMounted(updateRestaurantImages)

const handleImageClick = (market, name, index) => {
  const currentScroll = scroller.value.scrollLeft
  const newScroll = (scroller.value.clientWidth * 0.9) * index
  if (Math.abs(currentScroll - newScroll) > scroller.value.clientWidth * 0.5) {
    scroller.value.scrollLeft = (scroller.value.clientWidth * 0.9) * index
  } else {
    showRestaurantDetails(market, name)
  }
}

const showRestaurantDetails = (marketName, restaurantName) => {
  console.log('showRestaurantDetails', marketName, restaurantName)

  // find the restaurant
  let restaurant = restaurants.value.find(r => r.name == restaurantName && r.market == marketName)
  if (restaurant) {
    viewingDetailsRestaurantTab.value = 'Details'
    viewingDetailsRestaurant.value = restaurant
  }
}
</script>

<template>
  <div
    v-if="restaurantPlaceArray.length == 1"
    class="position-relative"
  >
    <VImg
      class="h-100 restaurant-image cursor-pointer"
      :src="restaurantPlaceArray?.[0].mainImageUrl"
      cover
      @click="showRestaurantDetails(restaurantMarket, restaurantName[0])"
    >
      <template #placeholder>
        <VRow
          class="fill-height ma-0"
          align="center"
          justify="center"
        >
          <VProgressCircular
            indeterminate
            color="grey-lighten-5"
          />
        </VRow>
      </template>
    </VImg>
  </div>
  <div
    v-else-if="restaurantPlaceArray.length > 1"
    ref="scroller"
    class="fanned-images-container"
    :class="[{'four-plus': restaurantPlaceArray.length >= 4}]"
  >
    <div
      v-for="(restaurantPlace, index) in restaurantPlaceArray"
      :key="index"
      class="position-relative fanned-image"
    >
      <VImg
        :src="restaurantPlace.mainImageUrl"
        class="h-100 cursor-pointer"
        cover
        :style="{ 'z-index': index }"
        @click="handleImageClick(restaurantMarket, restaurantName[index], index)"
      >
        <template #placeholder>
          <VRow
            class="fill-height ma-0"
            align="center"
            justify="center"
          >
            <VProgressCircular
              indeterminate
              color="grey-lighten-5"
            />
          </VRow>
        </template>
      </VImg>
    </div>
  </div>
</template>

<style lang="scss">
.restaurant-image-attribution {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 0.25em 0.5em 0.25em 0.75em;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 0.1rem;
  font-weight: 500;
  border-radius: 0.25rem;
  max-width: 100%;
}

.restaurant-image-attribution a {
  color: white;
}

.scrollable-images-container {
  height: 100%;
}

.fanned-images-container {
  overflow-x: auto;
  display: flex;
  height: 100%;
  gap: 0.1rem;
  scroll-snap-type: x mandatory;
  overscroll-behavior-x: none;
  -webkit-overflow-scrolling: touch;

}

.fanned-images-container::-webkit-scrollbar {
  display: none;
}

.fanned-image {
  min-width: 80%;
  scroll-snap-align: center;
  transition: all 200ms ease-in-out;
}

.fanned-image:first-child, .fanned-image:last-child {
  min-width: 90%;
}

@media only screen and (min-width: 600px) {
  .fanned-images-container:not(.four-plus){
    position: relative;
    display: flex;
    gap: 1px;
    height: 100%;
    width: 100%;
  }
  .fanned-images-container:not(.four-plus) .fanned-image {
    flex: 1;
    min-width: unset;
    transition: all 200ms ease-in-out;
  }

  .fanned-images-container:not(.four-plus):hover .fanned-image {
    flex: 0.2;
    transition: all 200ms;
  }

  .fanned-images-container:not(.four-plus):hover .fanned-image:hover {
    flex: 1;
  }

  .fanned-image .restaurant-image-attribution {
    opacity: 0;
    transition: opacity 200ms ease-in-out;
  }

  .fanned-image:hover .restaurant-image-attribution {
    opacity: 1;
  }
}
</style>
